import axios from "axios";
import { getCookie } from "../utils/cookieUtils";
import i18n from "../i18n";

const productApiBaseUrl = import.meta.env.VITE_PRODUCT_API;

const productApi = axios.create({
  baseURL: productApiBaseUrl,
});

productApi.interceptors.request.use((config) => {
  const token = getCookie("authToken");
  const language = i18n.language || "es"; // Use i18n's active language

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers["Accept-Language"] = language;
  return config;
});

export interface Product {
  id: string;
  title: string;
  brand: string;
  category: string;
  category_id: string;
  short_description: string;
  full_description: string;
  pictures: string[];
  sku: string;
  store: string;
  source_publication: string;
  cost: number;
  price: number;
  display_currency: string;
  display_price: number;
  display_total_charge?: number;
  recommended: boolean;
  related_products: string[];
  tags: string[];
  exclusive?: boolean;
  active?: boolean;
  picture_status?: Record<string, boolean>; // Status of pictures
  cover_picture?: string | null; // Index of the cover picture
}

export interface FiltersResponse {
  categories: string[];
  categoryIds: string[];
  brands: string[];
  tags: string[];
}

export interface Category {
  id: string;
  name: string;
  description: string;
  front_page: string;
}

interface ProductResponse {
  products: Product[];
  nextToken: string;
}

export interface ProductListParams {
  pageToken?: string | null;
  category?: string;
  categoryId?: string;
  brand?: string;
  recommended?: boolean;
  searchText?: string;
  tags?: string;
  exclusive?: boolean;
  pageSize?: number;
  price?: { min: number; max: number };
  sort?: { field: string; order: "asc" | "desc" };
}

export const listProducts = async (
  params: ProductListParams
): Promise<ProductResponse> => {
  try {
    const queryParams: Record<string, unknown> = {
      ...params,
      active: true,
      minPrice: params.price?.min,
      maxPrice: params.price?.max,
    };

    if (params.sort) {
      queryParams.sortField = params.sort.field;
      queryParams.sortOrder = params.sort.order;
    }

    const response = await productApi.get<ProductResponse>(
      "/api/product/list",
      { params: queryParams }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching product list", error);
    throw new Error("Failed to fetch product list");
  }
};

export const getFilters = async (): Promise<FiltersResponse> => {
  try {
    const response = await productApi.get<FiltersResponse>(
      "/api/product/filters"
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching filters", error);
    throw new Error("Failed to fetch filters");
  }
};

export const getProduct = async (id: string): Promise<Product> => {
  try {
    const response = await productApi.get<Product>("/api/product/get", {
      params: { id },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching product", error);
    throw new Error("Failed to fetch product");
  }
};

// New function for listing categories
export const listCategories = async (): Promise<Category[]> => {
  try {
    const response = await productApi.get<Category[]>("/api/category/list");
    return response.data;
  } catch (error) {
    console.error("Error fetching categories", error);
    throw new Error("Failed to fetch categories");
  }
};

// Count products by category
export const countProductsByCategory = async (
  categoryId: string
): Promise<number> => {
  try {
    const response = await productApi.get<{ count: number }>(
      "/api/product/count/category",
      {
        params: { category_id: categoryId },
      }
    );
    return response.data.count;
  } catch (error) {
    console.error("Error counting products by category", error);
    throw new Error("Failed to count products by category");
  }
};

// Count products by brand
export const countProductsByBrand = async (
  brandId: string
): Promise<number> => {
  try {
    const response = await productApi.get<{ count: number }>(
      "/api/product/count/brand",
      {
        params: { brand_id: brandId },
      }
    );
    return response.data.count;
  } catch (error) {
    console.error("Error counting products by brand", error);
    throw new Error("Failed to count products by brand");
  }
};

// Count all products
export const countAllProducts = async (): Promise<number> => {
  try {
    const response = await productApi.get<{ count: number }>(
      "/api/product/count/all"
    );
    return response.data.count;
  } catch (error) {
    console.error("Error counting all products", error);
    throw new Error("Failed to count all products");
  }
};
